import { useEffect, type ReactNode } from "react";
import { useMatches, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import "../i18n";
import { useDynamicCrumbs } from "./crumbs";
import { HelpPopover } from "./HelpPopover";
import { useAuth } from "../auth/AuthProvider";
import { useThemeSettings } from "../api/queries";
import { brandName as resolveName } from "../design/brand";
import { KeyValues } from "../design/components";

export type RouteHandle = { crumbs?: string[]; help?: string };

/** Route metadata (router.tsx) carries English crumbs and help text; the hub console looks
    each up in the catalog. Crumbs that are data (a company name, a document id) are never
    keys and stay as they are. */
const CRUMB_KEYS: Record<string, string> = {
  Hub: "hub",
  "Group Dashboard": "group_dashboard",
  Companies: "companies",
  Onboard: "onboard",
  Documents: "documents",
  Inbound: "inbound",
  Reports: "reports",
  "Users & Access": "users",
  Settings: "settings",
  "Theme Settings": "theme",
  Mapping: "mapping",
};
const HELP_KEYS: Record<string, string> = {
  "Group-wide volumes and the state of every company's connection. Figures cover the last 24 hours.": "help.dashboard",
};

function handleOf(match: { handle?: unknown }): RouteHandle {
  return (match.handle ?? {}) as RouteHandle;
}

export function Topbar({ title, pairs }: { title?: string | undefined; pairs?: [string, ReactNode][] }) {
  const { t } = useTranslation();
  const { me, signOut } = useAuth();
  const navigate = useNavigate();
  const matches = useMatches();
  const dynamic = useDynamicCrumbs();
  const isHub = me?.surface !== "portal";
  const theme = useThemeSettings({ enabled: isHub });
  const routeCrumbs = matches.map(handleOf).reduce<string[]>((acc, h) => h.crumbs ?? acc, []);
  const crumbs =
    dynamic ??
    (isHub ? routeCrumbs.map((crumb) => (CRUMB_KEYS[crumb] ? t(`crumbs.${CRUMB_KEYS[crumb]}`, crumb) : crumb)) : routeCrumbs);
  const rawHelp = matches.map(handleOf).reduce<string | undefined>((acc, h) => h.help ?? acc, undefined);
  const help = isHub && rawHelp && HELP_KEYS[rawHelp] ? t(HELP_KEYS[rawHelp], rawHelp) : rawHelp;
  const here = crumbs[crumbs.length - 1];
  // The portal is out of scope for translation (see the plan's "Portal scope" decision),
  // so it keeps its literal English strings.
  const envLabel = import.meta.env.VITE_HUB_ENV ?? (isHub ? t("topbar.selfHosted", "Self-hosted") : "Self-hosted");
  const signInPath = me?.surface === "portal" ? "/portal/signin" : "/signin";
  const breadcrumbLabel = isHub ? t("topbar.breadcrumb", "Breadcrumb") : "Breadcrumb";
  const signOutLabel = isHub ? t("topbar.signOut", "Sign out") : "Sign out";

  const brandName = isHub ? resolveName(t, theme.data?.appName) : "E-Invoicing Hub";
  useEffect(() => {
    const product = title || brandName;
    document.title = here ? `${here} — ${product}` : product;
  }, [here, title, brandName]);

  return (
    <div className="topbar">
      {title !== undefined ? (
        <div className="topbar-id">
          <span className="topbar-title">{title}</span>
          {pairs?.length ? <KeyValues flat pairs={pairs} /> : null}
        </div>
      ) : (
        <nav className="crumbs" aria-label={breadcrumbLabel}>
          {crumbs.map((crumb, i) =>
            i === crumbs.length - 1 ? (
              <span className="here" key={crumb} aria-current="page">
                {crumb}
              </span>
            ) : (
              <span key={crumb}>
                <span>{crumb}</span>
                <span className="sep"> / </span>
              </span>
            ),
          )}
        </nav>
      )}
      <div className="topbar-right">
        <HelpPopover text={help} />
        <span className="env">
          <span className="beacon">
            <i />
          </span>
          {envLabel}
        </span>
        {me ? <span className="who-name">{me.user.displayName}</span> : null}
        <button
          className="btn"
          type="button"
          onClick={async () => {
            await signOut();
            navigate(signInPath, { replace: true });
          }}
        >
          {signOutLabel}
        </button>
      </div>
    </div>
  );
}
